import React from "react";
import { Box } from "@chakra-ui/react";
import type { PageConfig } from "../../adminNext/screens/content-sections/editor/types";

interface CMSPageChromeProps {
  pageConfig: PageConfig | null;
  children: React.ReactNode;
}

/** Map legacy max_width values to a pixel string. */
function resolveLegacyMaxWidth(maxWidth: string | null | undefined): string {
  switch (maxWidth) {
    case "standard":
      return "1200px";
    case "wide":
      return "1440px";
    case "full":
    default:
      // No implicit max-width constraint — only constrain if explicitly configured
      return "100%";
  }
}

/**
 * Resolve container max width — prefers new container_max_width field,
 * falls back to legacy max_width for backward compat.
 */
function resolveContainerMaxWidth(pageConfig: PageConfig | null): string {
  const containerMW = pageConfig?.style?.container_max_width;
  if (containerMW) {
    // New field values are already CSS-ready ('full', '1440px', '1200px', '960px')
    return containerMW === 'full' ? '100%' : containerMW;
  }
  // Backward compat: fall back to legacy max_width
  return resolveLegacyMaxWidth(pageConfig?.style?.max_width);
}

export function CMSPageChrome({
  pageConfig,
  children,
}: CMSPageChromeProps): React.ReactElement {
  const showHeader = pageConfig?.chrome?.show_header ?? true;
  const showFooter = pageConfig?.chrome?.show_footer ?? true;
  const transparentHeader = pageConfig?.chrome?.transparent_header ?? false;
  const bgColor = pageConfig?.style?.background_color ?? "charcoal.charcoal";
  const maxWidth = resolveContainerMaxWidth(pageConfig);
  const paddingTop = pageConfig?.style?.padding_top ?? undefined;
  const paddingBottom = pageConfig?.style?.padding_bottom ?? undefined;

  return (
    <Box
      data-cms-header={showHeader}
      data-cms-footer={showFooter}
      data-cms-transparent-header={transparentHeader}
      bg={bgColor}
      minH="100vh"
      gc-testing-id="cmsPageChrome"
    >
      <Box
        maxW={maxWidth}
        mx="auto"
        pt={paddingTop}
        pb={paddingBottom}
      >
        {children}
      </Box>
    </Box>
  );
}
